import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "./icon";

// Dropdown menu, ported from the prototype's tc-styles.css `.dropdown`
// (topbar club switcher, row action menus). Closes on outside-click + Escape.
export interface DropdownProps {
  /** The element that toggles the menu (e.g. an IconButton or club-switch chip). */
  trigger: ReactNode;
  /** DropdownItem / DropdownSeparator children. */
  children: ReactNode;
  /** Anchor the menu to the trigger's left or right edge (default right). */
  align?: "left" | "right";
}

export function Dropdown({ trigger, children, align = "right" }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ all: "unset", cursor: "pointer", display: "inline-flex" }}
      >
        {trigger}
      </button>
      {open && (
        <div
          className="dropdown"
          role="menu"
          style={align === "left" ? { left: 0, right: "auto" } : undefined}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface DropdownItemProps {
  label: string;
  icon?: IconName;
  active?: boolean;
  onSelect?: () => void;
}

export function DropdownItem({ label, icon, active, onSelect }: DropdownItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn("dropdown-item", active && "active")}
      onClick={onSelect}
    >
      {icon && <Icon name={icon} size={16} />}
      {label}
    </button>
  );
}

export function DropdownSeparator() {
  return <div className="dropdown-sep" role="separator" />;
}
